import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import PageLoader from "components/ui/PageLoader";
import Modal from "components/ui/Modal";
import { Loader2 } from "lucide-react";
import { showToast } from "components/ui/toast";
import { getFileSignedUrl } from "apis/upload";
import { useGetEventsByPropertyId } from "apis/event";
import {
  useGetPrepareMarketingByPropertyIdQuery,
  useUploadEditedImageMutation,
  useSendEditedPhotosToReviewMutation,
  useGenerateWebFromPrintMutation,
  useHdrMergeMutation,
  useCreateEditorShotMutation,
  useFinalizeEditorShotsMutation,
  useUpdateEditorShotMutation,
  useUploadEditorShotOverrideMutation,
  useAiEditEditorShotsMutation,
  classifyEditorImage,
} from "apis/prepareMarketing";
import EditorUploadCard from "components/editorUpload/EditorUploadCard";
import {
  effectiveRawBatchStatus,
  isExtrasRawBatch,
} from "utils/prepareMarketing";
import { editorViewOf, newCaptureId, usesMediaFiles } from "utils/mediaEditorView";
import { deliverEditedPhotos } from "apis/media";

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "inProgress", label: "In Progress" },
  { key: "overdue", label: "Overdue" },
  { key: "revisionSent", label: "Revision Sent" },
  { key: "revisionInProgress", label: "Revision In Progress" },
  { key: "revisionCompleted", label: "Revision Completed" },
  { key: "completed", label: "Completed" },
];

const UploadEditor = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [signatureVisible, setSignatureVisible] = useState(false);
  const [signedSignatureUrl, setSignedSignatureUrl] = useState(null);
  const { propertyId } = useParams();
  const { data: events = [] } = useGetEventsByPropertyId(propertyId);
  const {
    data: fetchedMarketing,
    isPending: loading,
    refetch: refetchMarketing,
  } = useGetPrepareMarketingByPropertyIdQuery(propertyId);
  // A rental's files arrive as Media rows; the page renders their view.
  const marketing = useMemo(
    () => editorViewOf(fetchedMarketing),
    [fetchedMarketing],
  );
  const viewRef = React.useRef(marketing);
  viewRef.current = marketing;

  const uploadMutation = useUploadEditedImageMutation();
  const sendMutation = useSendEditedPhotosToReviewMutation();
  const generateWebMutation = useGenerateWebFromPrintMutation();
  const hdrMergeMutation = useHdrMergeMutation();
  const createEditorShotMutation = useCreateEditorShotMutation();
  const finalizeShotsMutation = useFinalizeEditorShotsMutation();
  const updateEditorShotMutation = useUpdateEditorShotMutation();
  const overrideMutation = useUploadEditorShotOverrideMutation();
  const aiEditMutation = useAiEditEditorShotsMutation();

  const apiAdapter = useMemo(
    () => ({
      uploadPrint: async (file, { propertyId: pid, onUploadProgress }) => {
        const presigned = await uploadMutation.mutateAsync({
          propertyId: pid,
          file,
          folderPath: "printPhotos",
          onUploadProgress: (e) => {
            const pct = Math.round(
              (e.loaded * 100) / (e.total || file.size || 1),
            );
            onUploadProgress?.(pct);
          },
        });
        const s3Key =
          presigned.key ||
          presigned.url.split("?")[0].split(".amazonaws.com/")[1];
        return {
          s3Key,
          url: presigned.url.split("?")[0],
          name: file.name,
          size: file.size,
        };
      },
      generateWebFromPrint: async (payload) =>
        generateWebMutation.mutateAsync(payload),
      mergeHdr: async (payload) => hdrMergeMutation.mutateAsync(payload),
      createEditorShot: (payload) =>
        createEditorShotMutation.mutateAsync(payload),
      finalizeShots: (payload) => finalizeShotsMutation.mutateAsync(payload),
      updateEditorShot: (payload) =>
        updateEditorShotMutation.mutateAsync(payload),
      uploadOverride: (payload) => overrideMutation.mutateAsync(payload),
      aiEditShots: (payload) => aiEditMutation.mutateAsync(payload),
      classifyImage: (imageUrl, imageId) =>
        classifyEditorImage(imageUrl, imageId),
      sendToReview: async ({
        propertyId: pid,
        eventId,
        editedWebPhotos,
        editedPrintPhotos,
        persistOnly = false,
      }) => {
        // A rental's edited photos are delivered to its Media rows.
        if (usesMediaFiles(viewRef.current)) {
          return deliverEditedPhotos({
            view: viewRef.current,
            propertyId: pid,
            eventId,
            editedWebPhotos,
            editedPrintPhotos,
            persistOnly,
          });
        }
        const res = await sendMutation.mutateAsync({
          propertyId: pid,
          editedWebPhotos,
          editedPrintPhotos,
          persistOnly,
        });
        if (res && res.success === false) {
          throw new Error(res.message || "Failed to send to review");
        }
        return res;
      },
    }),
    [
      uploadMutation,
      sendMutation,
      generateWebMutation,
      hdrMergeMutation,
      createEditorShotMutation,
      finalizeShotsMutation,
      updateEditorShotMutation,
      overrideMutation,
      aiEditMutation,
    ],
  );

  const eventsMap = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      map[e._id] = e;
    });
    return map;
  }, [events]);

  const maxPhotoId = useMemo(() => {
    const ids = [
      ...(marketing?.editedWebPhotos || []),
      ...(marketing?.editedPrintPhotos || []),
    ]
      .map((p) => Number(p.id))
      .filter((id) => Number.isFinite(id));
    return ids.length ? Math.max(...ids) : 0;
  }, [marketing?.editedWebPhotos, marketing?.editedPrintPhotos]);

  const nextPhotoIdRef = React.useRef(maxPhotoId);
  React.useEffect(() => {
    nextPhotoIdRef.current = Math.max(nextPhotoIdRef.current, maxPhotoId);
  }, [maxPhotoId]);
  const allocatePhotoId = React.useCallback(() => {
    // A rental's new capture is numbered from the id its row will be created with.
    if (usesMediaFiles(viewRef.current)) return newCaptureId();
    nextPhotoIdRef.current += 1;
    return nextPhotoIdRef.current;
  }, []);

  const rawPhotos = marketing?.rawPhotos || [];
  const statusForItem = (item) =>
    effectiveRawBatchStatus(
      item,
      eventsMap[item.eventId]?.status,
      marketing,
    );
  const getTabCount = (key) => {
    if (key === "all") return rawPhotos.length;
    return rawPhotos.filter((item) => statusForItem(item) === key).length;
  };
  const filteredPhotos =
    activeTab === "all"
      ? rawPhotos
      : rawPhotos.filter((item) => statusForItem(item) === activeTab);

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <p className="mb-4 text-center">{marketing?.propertyId?.address}</p>
      {rawPhotos.length > 1 && (
        <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1">
          {STATUS_TABS.map(({ key, label }) => {
            const count = getTabCount(key);
            if (key !== "all" && count === 0) return null;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === key
                    ? "bg-black text-white border-black"
                    : "bg-transparent text-darkergray border-mediumgray hover:border-black hover:text-black"
                }`}
              >
                {label}
                {count > 0 && (
                  <span
                    className={`ml-1 text-[10px] ${
                      activeTab === key ? "text-white/60" : "text-darkergray"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      <div className="w-full flex gap-6 flex-col">
        {filteredPhotos.map((item, index) => {
          const array = rawPhotos;
          const matchIndex = array.findIndex((p) => p.eventId === item.eventId);
          const existingCount =
            matchIndex > 0
              ? array.slice(0, matchIndex).reduce((total, prev) => {
                  return total + (prev.editCount || 0);
                }, 0)
              : 0;
          const event = eventsMap[item.eventId] || null;
          const enrichedItem = {
            ...item,
            status: effectiveRawBatchStatus(item, event?.status, marketing),
            event,
          };
          const commentedFromApi = (marketing?.rawPhotosComments || [])
            .filter((c) => String(c.eventId) === String(item.eventId))
            .map((c) => ({
              imageId: c.imageId,
              comments: c.comments || [],
              paidRevisions: c.paidRevisions || [],
            }));
          const extrasBatch = isExtrasRawBatch(item);

          const signedApprovalBtn = event?.imageApproval?.signature ? (
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                setSignedSignatureUrl(null);
                setSignatureVisible(true);
                try {
                  const signedUrl = await getFileSignedUrl(
                    event.imageApproval.signature,
                  );
                  setSignedSignatureUrl(signedUrl);
                } catch (error) {
                  console.error("Error:", error);
                  showToast("error", "Failed to load signature");
                }
              }}
              className="bg-green-50 text-green-600 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-green-200"
            >
              Signed Approval
            </button>
          ) : null;

          return (
            <EditorUploadCard
              key={item.eventId || index}
              item={enrichedItem}
              address={marketing?.propertyId?.address}
              propertyId={marketing?.propertyId?._id}
              // Photos the contractor sent straight to the marketing link
              // are not this batch's deliverables — left in, they would fill
              // the editor's empty capture slots.
              editedWebPhotos={marketing?.editedWebPhotos?.filter(
                (p) =>
                  String(p.eventId) === String(item.eventId) &&
                  !p.directToMarketing,
              )}
              editedPrintPhotos={marketing?.editedPrintPhotos?.filter(
                (p) =>
                  String(p.eventId) === String(item.eventId) &&
                  !p.directToMarketing,
              )}
              hdrMerges={marketing?.hdrMerges?.filter(
                (merge) => String(merge.eventId) === String(item.eventId),
              )}
              editorShots={marketing?.editorShots?.filter(
                (shot) => String(shot.eventId) === String(item.eventId),
              )}
              existingCount={existingCount}
              maxPhotoId={maxPhotoId}
              allocatePhotoId={allocatePhotoId}
              extraPhotos={
                extrasBatch &&
                ![
                  "revisionSent",
                  "revisionInProgress",
                  "revisionCompleted",
                ].includes(enrichedItem.status)
                  ? marketing?.extraPhotos || []
                  : []
              }
              commentedRawPhotos={commentedFromApi}
              api={apiAdapter}
              headerExtra={signedApprovalBtn}
              onRefresh={refetchMarketing}
            />
          );
        })}
        {filteredPhotos.length === 0 && (
          <div className="text-center py-12 bg-lightergray rounded-lg border border-dashed border-mediumgray">
            <p className="m-0 text-sm text-darkergray font-medium">
              No items with this status.
            </p>
          </div>
        )}
      </div>

      <Modal
        isOpen={signatureVisible}
        onClose={() => setSignatureVisible(false)}
        title="Signed Approval"
      >
        <div className="p-4 flex justify-center">
          {signedSignatureUrl ? (
            <img
              src={signedSignatureUrl}
              alt="Signature"
              className="max-w-full max-h-80 object-contain"
            />
          ) : (
            <Loader2 className="animate-spin w-8 h-8 text-darkergray" />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default UploadEditor;
